import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { FaCertificate } from 'react-icons/fa'
import HeroCarousel from '@/components/common/HeroCarousel'

const Home = () => {
  const { t } = useTranslation()

  const carouselSlides = [
    {
      image: '../assets/License Plates for Every Vehicle.webp',
      title: 'Willkommen bei KennzeichenPro24',
      subtitle: 'Ihr Partner für Fahrzeugzulassung und Zubehör',
    },
  ]

  const services = [
    {
      icon: <FaCertificate className="w-10 h-10" />,
      title: t('home.vehicleRegistration'),
      description: t('home.vehicleRegistrationDesc'),
      href: '/contact',
      gradient: 'from-blue-500 via-blue-600 to-blue-700',
      glow: 'shadow-blue-500/50',
    },
  ]

  const features = [
    {
      icon: '24/7',
      title: t('home.support24'),
      description: t('home.support24Desc'),
      delay: 0.1,
      color: 'text-neon-blue',
    },
    {
      icon: '✓',
      title: t('home.quality'),
      description: t('home.qualityDesc'),
      delay: 0.2,
      color: 'text-neon-green',
    },
    {
      icon: '🚚',
      title: t('home.fastShipping'),
      description: t('home.fastShippingDesc'),
      delay: 0.3,
      color: 'text-primary-400',
    },
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  }

  return (
    <div className="bg-dark-950 min-h-screen relative overflow-hidden">
      {/* Hero Carousel */}
      <HeroCarousel slides={carouselSlides} autoPlayInterval={5000} />

      {/* Services Section */}
      <section className="relative py-12 sm:py-16 bg-dark-900/50 backdrop-blur-sm">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 sm:mb-12"
          >
            <h2 className="section-title">{t('home.services')}</h2>
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-primary-500 to-transparent mx-auto"></div>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -10, scale: 1.02 }}
                className="group"
              >
                <Link to={service.href}>
                  <div className="card-hover h-full p-4 sm:p-6 relative overflow-hidden">
                    {/* Glow effect */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500 blur-2xl`}></div>

                    <motion.div
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.5 }}
                      className={`relative inline-flex p-4 rounded-2xl bg-gradient-to-br ${service.gradient} text-white mb-4 shadow-lg ${service.glow}`}
                    >
                      {service.icon}
                    </motion.div>

                    <h3 className="relative text-xl font-bold mb-3 text-gray-100 group-hover:text-primary-400 transition-colors">
                      {service.title}
                    </h3>
                    <p className="relative text-gray-400 leading-relaxed text-sm">{service.description}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-12 sm:py-16 bg-dark-950">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 sm:mb-12"
          >
            <h2 className="section-title">{t('home.features')}</h2>
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-primary-500 to-transparent mx-auto"></div>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: feature.delay, duration: 0.6 }}
                whileHover={{ scale: 1.03, y: -5 }}
                className="text-center card-hover p-8 relative overflow-hidden group"
              >
                {/* Animated background */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 3, repeat: Infinity, delay: feature.delay }}
                  className={`relative text-4xl sm:text-5xl font-black mb-3 sm:mb-4 ${feature.color}`}
                  style={{ textShadow: '0 0 15px currentColor' }}
                >
                  {feature.icon}
                </motion.div>
                <h3 className="relative text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-gray-100">{feature.title}</h3>
                <p className="relative text-gray-400 leading-relaxed text-xs sm:text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home
